/**
 * API Compatibility Layer
 *
 * This file re-exports the generated API hooks with names that match
 * the legacy API for backwards compatibility during migration.
 *
 * Generated from Django OpenAPI schema via @rtk-query/codegen-openapi
 */

import { generatedApi } from './generatedApi';

// Re-export the generated API as the main API
export { generatedApi };

// Also export as 'api' for backwards compatibility with util methods
// (e.g., api.util.updateQueryData, api.util.patchQueryData)
export const api = generatedApi;

// ============================================================================
// HOOK ALIASES - Maps old names to new generated names
// ============================================================================

// --- Authentication ---
export {
  useAuthLoginCreateMutation as useLoginMutation,
  useAuthRegisterCreateMutation as useRegisterMutation,
  useAuthForgotPasswordCreateMutation as useForgotPasswordMutation,
  useAuthResetPasswordCreateMutation as useResetPasswordMutation,
  useAuthChangePasswordCreateMutation as useChangePasswordMutation,
  useAuthConfirmEmailCreateMutation as useConfirmEmailMutation,
  useAuthResendCodeCreateMutation as useResendCodeMutation,
  useAuthIsVerifiedRetrieveQuery as useIsVerifiedQuery,
  useAuthRefreshCreateMutation as useRefreshTokenMutation,
} from './generatedApi';

// --- User ---
export {
  useUsersMeRetrieveQuery as useGetMeQuery,
  useUsersMePartialUpdateMutation as useUpdateMeMutation,
} from './generatedApi';

// --- Billing ---
export {
  useBillingBalanceRetrieveQuery as useGetBalanceQuery,
  useBillingCheckoutCreateMutation as useCreateCheckoutMutation,
  useBillingSubscriptionCancelCreateMutation as useCancelSubscriptionMutation,
  useBillingPlansRetrieveQuery as useGetPlansQuery,
  useBillingAddTokensCreateMutation as useAddTokensMutation,
} from './generatedApi';

// Use djangoApi's transaction query which properly passes page/limit params
export { useGetTransactionHistoryQuery as useGetTransactionsQuery } from './djangoApi';

// --- Subscription ---
export {
  useSubscriptionPackagesRetrieveQuery as useGetPackagesQuery,
  useSubscriptionPricesRetrieveQuery as useGetPricesQuery,
  useSubscriptionSubscriptionRetrieveQuery as useGetSubscriptionQuery,
} from './generatedApi';

// --- Campaigns ---
export {
  useCampaignsListQuery as useGetCampaignsQuery,
  useCampaignsCreateMutation as useCreateCampaignMutation,
  useCampaignsRetrieveQuery as useGetCampaignQuery,
  useCampaignsUpdateMutation as useUpdateCampaignFullMutation,
  useCampaignsPartialUpdateMutation as useUpdateCampaignMutation,
  useCampaignsDestroyMutation as useDeleteCampaignMutation,
  useCampaignsStartCreateMutation as useStartCampaignMutation,
  useCampaignsStopCreateMutation as useStopCampaignMutation,
  useCampaignsStatusRetrieveQuery as useGetCampaignStatusQuery,
  useCampaignsLeadsRetrieveQuery as useGetCampaignLeadsQuery,
  useCampaignsFlowConfigCreateMutation as useSetCampaignFlowConfigMutation,
  useCampaignsLinkEmailCreateMutation as useLinkEmailToCampaignMutation,
  useCampaignsPublishLeadsCreateMutation as usePublishLeadsMutation,
  useCampaignsTypesRetrieveQuery as useGetCampaignTypesInfoQuery,
} from './generatedApi';

// --- Campaign Types ---
// Note: Generated hooks return 'unknown' type, we create typed wrappers below
import {
  useCampaignTypesRetrieveQuery as _useCampaignTypesRetrieveQuery,
  useCampaignTypesProperRetrieveQuery as _useCampaignTypesProperRetrieveQuery,
  useLegacyCampaignTypesRetrieveQuery as _useLegacyCampaignTypesRetrieveQuery,
} from './generatedApi';

// Typed wrapper hooks for campaign types (schema returns unknown, components expect arrays)
// We override only the data property while preserving all other RTK Query result properties
type TypedQueryResult<T> = Omit<ReturnType<typeof _useCampaignTypesRetrieveQuery>, 'data'> & { data: T };

export const useGetCampaignTypesQuery = (
  arg?: void,
  options?: Parameters<typeof _useCampaignTypesRetrieveQuery>[1],
): TypedQueryResult<CampaignTypesDto[]> => {
  const result = _useCampaignTypesRetrieveQuery(arg, options);
  return { ...result, data: (result.data || []) as CampaignTypesDto[] };
};
export const useGetCampaignTypeQuery = useGetCampaignTypesQuery;
export const useGetCampaignTypesProperQuery = (
  arg?: void,
  options?: Parameters<typeof _useCampaignTypesProperRetrieveQuery>[1],
): TypedQueryResult<CampaignTypesDto[]> => {
  const result = _useCampaignTypesProperRetrieveQuery(arg, options);
  return { ...result, data: (result.data || []) as CampaignTypesDto[] };
};
export const useGetLegacyCampaignTypesQuery = (
  arg?: void,
  options?: Parameters<typeof _useLegacyCampaignTypesRetrieveQuery>[1],
): TypedQueryResult<CampaignTypesDto[]> => {
  const result = _useLegacyCampaignTypesRetrieveQuery(arg, options);
  return { ...result, data: (result.data || []) as CampaignTypesDto[] };
};

// --- Campaign Filters & Sources ---
export {
  useCampaignFiltersRetrieveQuery as useGetCampaignFiltersQuery,
  useCampaignSourcesRetrieveQuery as useGetCampaignSourcesQuery,
} from './generatedApi';

// --- Leads ---
// Custom useGetLeadsQuery that properly accepts query parameters
// The generated API's leadsList doesn't accept params, so we inject a custom endpoint
import { emptyBaseSplitApi } from './baseApi';

// Extend the API with properly parameterized leads endpoints
// The generated API's endpoints don't accept query params, so we inject custom ones
const leadsApiWithParams = emptyBaseSplitApi.injectEndpoints({
  endpoints: build => ({
    getLeadsWithParams: build.query<
      any,
      { campaign_id?: string; page?: number; page_size?: number; scraperId?: string }
    >({
      query: params => ({
        url: `/api/leads/`,
        params: {
          campaign_id: params.campaign_id,
          page: params.page,
          page_size: params.page_size,
          scraperId: params.scraperId,
        },
      }),
    }),
    getLeadsDatabaseWithParams: build.query<
      any,
      { page?: number; limit?: number; campaignType?: number; filters?: string; search?: string }
    >({
      query: params => ({
        url: `/api/leads/database/`,
        params: {
          page: params.page,
          limit: params.limit,
          campaignType: params.campaignType,
          ...(params.filters && Object.fromEntries(new URLSearchParams(decodeURIComponent(params.filters)))),
          ...(params.search && { search: params.search }),
        },
      }),
    }),
    getMyLeadsWithParams: build.query<
      any,
      { page?: number; limit?: number; campaignType?: number; filters?: string; search?: string }
    >({
      query: params => ({
        url: `/api/leads/mine/`,
        params: {
          page: params.page,
          limit: params.limit,
          campaignType: params.campaignType,
          ...(params.filters && Object.fromEntries(new URLSearchParams(decodeURIComponent(params.filters)))),
          ...(params.search && { search: params.search }),
        },
      }),
    }),
    getLeadsFilterOptionsWithParams: build.query<any, { campaignType?: number } | void>({
      query: params => ({
        url: `/api/leads/filter-options/`,
        params: params ? { campaignType: params.campaignType } : undefined,
      }),
    }),
  }),
  overrideExisting: true,
});

export const useGetLeadsQuery = leadsApiWithParams.useGetLeadsWithParamsQuery;
export const useGetLeadsDatabaseQuery = leadsApiWithParams.useGetLeadsDatabaseWithParamsQuery;
export const useGetMyLeadsQuery = leadsApiWithParams.useGetMyLeadsWithParamsQuery;
export const useGetLeadsFilterOptionsQuery = leadsApiWithParams.useGetLeadsFilterOptionsWithParamsQuery;

// --- AI email-variant generation (not in generated schema; inject manually) ---
export interface GeneratedVariant {
  label: string;
  subject: string;
  body: string;
}

const emailVariantsApi = emptyBaseSplitApi.injectEndpoints({
  endpoints: build => ({
    generateEmailVariants: build.mutation<
      { variants: GeneratedVariant[] },
      { subject: string; body: string; count?: number; angle?: string }
    >({
      query: body => ({
        url: `/api/email-campaigns/generate-variants/`,
        method: 'POST',
        body,
      }),
    }),
  }),
  overrideExisting: true,
});

export const useGenerateEmailVariantsMutation = emailVariantsApi.useGenerateEmailVariantsMutation;

export {
  useLeadsCreateMutation as useCreateLeadMutation,
  useLeadsRetrieveQuery as useGetLeadQuery,
  useLeadsUpdateMutation as useUpdateLeadFullMutation,
  useLeadsPartialUpdateMutation as useUpdateLeadMutation,
  useLeadsDestroyMutation as useDeleteLeadMutation,
  useLeadsBulkCreateCreateMutation as useBulkCreateLeadsMutation,
} from './generatedApi';

// --- Scrapeking Database ---
export {
  useScrapekingDatabaseRetrieveQuery as useGetScrapekingDatabaseQuery,
  // useScrapekingDatabaseFilterOptionsRetrieveQuery removed from schema
  useScrapekingDatabaseStatsRetrieveQuery as useGetScrapekingDbStatsQuery,
} from './generatedApi';
// Stub for removed hook
export const useGetScrapekingDbFilterOptionsQuery = () => ({ data: undefined, isLoading: false, error: null });

// --- Marketplace ---
export {
  useMarketplaceRetrieveQuery as useGetMarketplaceQuery,
  useMarketplacePurchaseCreateMutation as usePurchaseLeadsMutation,
  useMarketplacePurchasedRetrieveQuery as useGetPurchasedLeadsQuery,
  useMarketplaceAnalyticsRetrieveQuery as useGetMarketplaceAnalyticsQuery,
} from './generatedApi';

// --- Scrapers ---
export {
  useScrapersRetrieveQuery as useGetScrapersQuery, // List removed, using retrieve
  useScrapersRetrieveQuery as useGetScraperQuery,
  useScrapersRetrieveQuery as useGetScraperConfigQuery, // Config removed
  useScrapersRetrieveQuery as useGetLegacyScrapersQuery, // Legacy list removed
} from './generatedApi';

// --- Scraper Templates ---
export {
  useScraperTemplatesRetrieveQuery as useGetScraperTemplatesQuery, // List removed
  useScraperTemplatesRetrieveQuery as useGetScraperTemplateQuery,
} from './generatedApi';
// Stubs for removed hooks
export const useGetScraperTemplateFiltersQuery = () => ({ data: undefined, isLoading: false, error: null });
export const useGetScraperTemplateCategoriesQuery = () => ({ data: undefined, isLoading: false, error: null });

// --- Scraper Tasks ---
export {
  useScrapersTasksListQuery as useGetScraperTasksQuery,
  useScrapersTasksCreateMutation as useCreateScraperTaskMutation,
  useScrapersTasksRetrieveQuery as useGetScraperTaskQuery,
  useScrapersTasksCancelCreateMutation as useCancelScraperTaskMutation,
  useScrapersTasksResultsRetrieveQuery as useGetScraperTaskResultsQuery,
  useScrapersTasksRetryCreateMutation as useRetryScraperTaskMutation,
  useScrapersTasksStatusCheckRetrieveQuery as useCheckScraperTaskStatusQuery,
  useScrapersTasksExecuteCampaignCreateMutation as useExecuteCampaignTaskMutation,
  useScrapersTasksStatsRetrieveQuery as useGetScraperTaskStatsQuery,
} from './generatedApi';

// --- Scraper Schedules ---
export {
  useScrapersSchedulesListQuery as useGetScraperSchedulesQuery,
  useScrapersSchedulesCreateMutation as useCreateScraperScheduleMutation,
  useScrapersSchedulesRetrieveQuery as useGetScraperScheduleQuery,
  useScrapersSchedulesUpdateMutation as useUpdateScraperScheduleFullMutation,
  useScrapersSchedulesPartialUpdateMutation as useUpdateScraperScheduleMutation,
  useScrapersSchedulesDestroyMutation as useDeleteScraperScheduleMutation,
  useScrapersSchedulesActivateCreateMutation as useActivateScraperScheduleMutation,
  useScrapersSchedulesDeactivateCreateMutation as useDeactivateScraperScheduleMutation,
  useScrapersSchedulesRunNowCreateMutation as useRunScraperScheduleNowMutation,
} from './generatedApi';

// --- Jobs --- (Many endpoints removed from schema)
export {
  useJobsRetrieveQuery as useGetJobsQuery,
  useJobsRetrieveQuery as useGetJobQuery,
  useJobsRetrieve2Query as useGetJobDetailsQuery,
} from './generatedApi';
// Stubs for removed hooks
export const useCreateJobMutation = () => [() => {}, { isLoading: false }];
export const useUpdateJobFullMutation = () => [() => {}, { isLoading: false }];
export const useUpdateJobMutation = () => [() => {}, { isLoading: false }];
export const useDeleteJobMutation = () => [() => {}, { isLoading: false }];
export const useCancelJobMutation = () => [() => {}, { isLoading: false }];
export const useGetJobExportQuery = () => ({ data: undefined, isLoading: false, error: null });
export const useGetJobResultsQuery = () => ({ data: undefined, isLoading: false, error: null });
export const useGetJobStatsQuery = () => ({ data: undefined, isLoading: false, error: null });

// --- Hybrid Scraping --- (Removed from schema)
// Stubs for removed hooks
export const useStartHybridScrapeMutation = () => [() => {}, { isLoading: false }];
export const useGetHybridSpidersQuery = () => ({ data: undefined, isLoading: false, error: null });
export const useGetHybridExportQuery = () => ({ data: undefined, isLoading: false, error: null });
export const useGetHybridStatsQuery = () => ({ data: undefined, isLoading: false, error: null });

// --- Enrichment ---
export {
  useEnrichmentVerifyEmailCreateMutation as useVerifyEmailsMutation,
  useEnrichmentLeadCreateMutation as useEnrichLeadMutation,
  useEnrichmentBatchCreateMutation as useEnrichBatchMutation,
  useEnrichmentSmartCreateMutation as useSmartEnrichMutation,
  useEnrichmentSmartBatchCreateMutation as useSmartEnrichBatchMutation,
  useEnrichmentEnhancedCreateMutation as useEnhancedEnrichMutation,
  useEnrichmentEnhancedBatchCreateMutation as useEnhancedEnrichBatchMutation,
  useEnrichmentEnhancedStatsRetrieveQuery as useGetEnhancedEnrichStatsQuery,
  useEnrichmentFindDomainCreateMutation as useFindDomainMutation,
  useEnrichmentJobCreateMutation as useCreateEnrichmentJobMutation,
  useEnrichmentJobRetrieveQuery as useGetEnrichmentJobQuery,
  useEnrichmentStatsRetrieveQuery as useGetEnrichmentStatsQuery,
} from './generatedApi';

// --- Email Campaigns ---
export {
  useEmailCampaignsEmailCampaignsListQuery as useGetEmailCampaignsQuery,
  useEmailCampaignsEmailCampaignsCreateMutation as useCreateEmailCampaignMutation,
  useEmailCampaignsEmailCampaignsRetrieveQuery as useGetEmailCampaignQuery,
  useEmailCampaignsEmailCampaignsUpdateMutation as useUpdateEmailCampaignFullMutation,
  useEmailCampaignsEmailCampaignsPartialUpdateMutation as useUpdateEmailCampaignMutation,
  useEmailCampaignsEmailCampaignsDestroyMutation as useDeleteEmailCampaignMutation,
  useEmailCampaignsEmailCampaignsStartCreateMutation as useStartEmailCampaignMutation,
  useEmailCampaignsEmailCampaignsPauseCreateMutation as usePauseEmailCampaignMutation,
  useEmailCampaignsEmailCampaignsResumeCreateMutation as useResumeEmailCampaignMutation,
  useEmailCampaignsEmailCampaignsCancelCreateMutation as useCancelEmailCampaignMutation,
  useEmailCampaignsEmailCampaignsCloneCreateMutation as useCloneEmailCampaignMutation,
  useEmailCampaignsEmailCampaignsLeadsListQuery as useGetEmailCampaignLeadsQuery,
  useEmailCampaignsEmailCampaignsLeadsCreateMutation as useAddEmailCampaignLeadsMutation,
  useEmailCampaignsEmailCampaignsStatsRetrieveQuery as useGetEmailCampaignStatsQuery,
  useEmailCampaignsEmailCampaignsRequestRefundCreateMutation as useRequestEmailCampaignRefundMutation,
} from './generatedApi';

// --- Email Analytics ---
export {
  useEmailCampaignsEmailAnalyticsOverviewRetrieveQuery as useGetEmailAnalyticsOverviewQuery,
  useEmailCampaignsEmailAnalyticsCampaignRetrieveQuery as useGetEmailCampaignAnalyticsQuery,
  // Alias for legacy component compatibility
  useEmailCampaignsEmailAnalyticsOverviewRetrieveQuery as useGetAnalyticsOverviewQuery,
} from './generatedApi';

// --- Master Inbox ---
export {
  useEmailCampaignsMasterInboxAccountsRetrieveQuery as useGetMasterInboxAccountsQuery,
  useEmailCampaignsMasterInboxThreadsRetrieveQuery as useGetMasterInboxThreadsQuery,
  useEmailCampaignsMasterInboxThreadRetrieveQuery as useGetMasterInboxThreadQuery,
  useEmailCampaignsMasterInboxReplyCreateMutation as useSendMasterInboxReplyMutation,
  useEmailCampaignsMasterInboxMarkReadCreateMutation as useMarkMasterInboxReadMutation,
  // Aliases for legacy component compatibility
  useEmailCampaignsMasterInboxThreadsRetrieveQuery as useGetEmailThreadsQuery,
  useEmailCampaignsMasterInboxReplyCreateMutation as useSendReplyMutation,
  useEmailCampaignsMasterInboxMarkReadCreateMutation as useMarkAsReadMutation,
} from './generatedApi';

// --- Email Accounts (Django proxies to NestJS email service) ---
export {
  useEmailCampaignsAccountsListQuery as useGetEmailAccountsQuery,
  useEmailCampaignsAccountsRetrieveQuery as useGetEmailAccountQuery,
  useEmailCampaignsAccountsDestroyMutation as useDeleteEmailAccountMutation,
  useEmailCampaignsAccountsTestCreateMutation as useTestEmailAccountMutation,
  useEmailCampaignsAccountsWarmupStatusRetrieveQuery as useGetWarmupStatusQuery,
  useEmailCampaignsAccountsStartWarmupCreateMutation as useStartWarmupMutation,
  useEmailCampaignsAccountsPauseWarmupCreateMutation as usePauseWarmupMutation,
  useEmailCampaignsAccountsImapSmtpCreateMutation as useCreateImapSmtpAccountMutation,
  // OAuth endpoints
  useEmailCampaignsAccountsOauthGmailInitCreateMutation as useInitGmailOAuthMutation,
  useEmailCampaignsAccountsOauthGmailCallbackCreateMutation as useCompleteGmailOAuthMutation,
  useEmailCampaignsAccountsOauthOutlookInitCreateMutation as useInitOutlookOAuthMutation,
  useEmailCampaignsAccountsOauthOutlookCallbackCreateMutation as useCompleteOutlookOAuthMutation,
} from './generatedApi';

// --- Email Projects (from manual emailApi - not yet in generated types) ---
export {
  useGetEmailProjectsQuery,
  useGetEmailProjectQuery,
  useCreateEmailProjectMutation,
  useUpdateEmailProjectMutation,
  useDeleteEmailProjectMutation,
  useBulkAssignAccountsToProjectMutation,
  useRemoveAccountsFromProjectMutation,
  useAssignAccountToProjectMutation,
} from './emailApi';
export type { EmailProject } from './emailApi';

// --- Organizations ---
export {
  useOrganizationsListQuery as useGetOrganizationsQuery,
  useOrganizationsCreateMutation as useCreateOrganizationMutation,
  useOrganizationsRetrieveQuery as useGetOrganizationQuery,
  useOrganizationsUpdateMutation as useUpdateOrganizationFullMutation,
  useOrganizationsPartialUpdateMutation as useUpdateOrganizationMutation,
  useOrganizationsDestroyMutation as useDeleteOrganizationMutation,
  useOrganizationsCurrentRetrieveQuery as useGetCurrentOrganizationQuery,
  useOrganizationsStatisticsRetrieveQuery as useGetOrganizationStatisticsQuery,
  useOrganizationsInviteMemberCreateMutation as useInviteOrganizationMemberMutation,
  useOrganizationsCreateOrganizationCreateMutation as useCreateOrganizationV2Mutation,
} from './generatedApi';

// --- Templates --- (Most endpoints removed from schema)
export {
  useTemplatesRetrieveQuery as useGetTemplatesQuery,
  useTemplatesRetrieve2Query as useGetTemplateQuery,
} from './generatedApi';
// Stubs for removed hooks
export const useCreateTemplateMutation = () => [() => {}, { isLoading: false }];
export const useUpdateTemplateFullMutation = () => [() => {}, { isLoading: false }];
export const useUpdateTemplateMutation = () => [() => {}, { isLoading: false }];
export const useDeleteTemplateMutation = () => [() => {}, { isLoading: false }];
export const useGetTemplateByDomainQuery = () => ({ data: undefined, isLoading: false, error: null });

// --- Monitoring ---
export {
  useMonitoringListQuery as useGetMonitoringListQuery,
  useMonitoringRetrieveQuery as useGetMonitoringQuery,
  useMonitoringCancelCreateMutation as useCancelMonitoringMutation,
  useMonitoringStatisticsRetrieveQuery as useGetMonitoringStatisticsQuery,
} from './generatedApi';

// --- Proxy ---
export {
  useProxyRetrieveQuery as useGetProxyQuery,
  useProxyCreateMutation as useCreateProxyMutation,
} from './generatedApi';

// --- Health ---
export { useHealthRetrieveQuery as useHealthCheckQuery } from './generatedApi';

// ============================================================================
// LEGACY DJANGO API HOOKS (for endpoints not in OpenAPI schema)
// These will be removed once Django OpenAPI schema is complete
// ============================================================================
export {
  // Email verification via Debounce
  useVerifyEmailsDebounceMutation,
  useGetDebouncePricingQuery,
  // Token costs
  useGetTokenCostsQuery,
  // Invoice details
  useGetInvoiceDetailsQuery,
} from './djangoApi';

// Alias for topup - use the add tokens mutation from generated API
export { useBillingAddTokensCreateMutation as useCreateTopupMutation } from './generatedApi';

// ============================================================================
// ADDITIONAL HOOK ALIASES (for legacy code compatibility)
// ============================================================================

// Campaign details query alias (same as retrieve)
export { useCampaignsRetrieveQuery as useGetCampaignDetailsQuery } from './generatedApi';

// Purchase all leads alias (same as purchase)
export { useMarketplacePurchaseCreateMutation as usePurchaseAllLeadsMutation } from './generatedApi';

// ============================================================================
// CUSTOM TYPES (for APIs where generated types are incomplete)
// ============================================================================

/**
 * Balance response from /api/billing/balance/
 * The OpenAPI schema doesn't define this properly, so we define it manually
 */
export interface Balance {
  balance: number;
  token: number;
  reserved: number;
}

/**
 * Paginated leads response - stub type for compatibility
 * Supports both Django REST Framework (count/results) and custom (total/data) formats
 */
export interface PaginatedLeadsDto {
  count?: number;
  total?: number;
  page?: number;
  limit?: number;
  totalPages?: number;
  next?: string | null;
  previous?: string | null;
  results?: any[];
  data?: any[];
}

/**
 * Checkout creation DTO - stub type for compatibility
 */
export interface CheckoutCreateDto {
  priceId: string;
  successUrl?: string;
  cancelUrl?: string;
}

/**
 * Topup DTO - stub type for compatibility
 */
export interface TopupDto {
  priceId?: string;
  amount?: number;
  tokenPackageId?: string;
  successUrl?: string;
  cancelUrl?: string;
}

/**
 * Email verification result - stub type for compatibility
 */
export interface EmailVerificationResult {
  email: string;
  is_valid: boolean;
  is_disposable?: boolean;
  is_role?: boolean;
  free_email?: boolean;
  status?: string;
  reason?: string;
  code?: string;
}

/**
 * Debounce verification result - stub type for compatibility
 */
export interface DebounceVerificationResult {
  email: string;
  code: string;
  result?: string;
  reason?: string;
  is_disposable?: boolean;
  is_role?: boolean;
  free_email?: boolean;
}

/**
 * Campaign filters DTO - stub type for compatibility
 * Supports both new (id/name) and legacy (campaign_filters_id/key) formats
 */
export interface CampaignFiltersDto {
  // New format
  id?: number;
  name?: string;
  type?: string;
  scraper_id?: number;
  price?: number;
  is_free?: boolean;
  // Legacy format
  campaign_filters_id?: number;
  key?: string;
  campaign_type_id_campaign_type?: number;
  required?: boolean;
  placeholder?: string;
  // Filter display format
  filter_name?: string;
  filter_val?: any;
  value?: any;
}

/**
 * Campaign filter DTO alias - legacy name
 */
export type CampFilterDto = CampaignFiltersDto;

/**
 * Campaign types DTO - stub type for campaign type dropdown
 * Supports both new format (id/name) and legacy format (campaign_type_id/type_name)
 */
export interface CampaignTypesDto {
  id?: number;
  name?: string;
  type_name?: string; // Alias for name (legacy format)
  description?: string;
  icon?: string;
  category?: string;
  campaign_type_id?: number; // Legacy format
  price?: number;
  status?: string;
}

/**
 * Create scheduling DTO - stub type for campaign scheduling
 */
export interface CreateSchedulingDto {
  repeat?: 'Once' | 'Weekly' | 'Monthly' | string;
  repeat_count?: number;
  start_date?: string;
  days?: number[];
  schedule_type?: string;
}

/**
 * Create campaign DTO - stub type for campaign creation
 */
export interface CreateCampaignDto {
  name: string;
  campaign_type?: number | string;
  campaign_type_id?: number;
  description?: string;
  status?: string | number;
  config?: Record<string, any>;
  filters?: Record<string, any>;
  camp_filters?: CampaignFiltersDto[];
  campaign_scrapers?: any[];
  schedule_type?: string;
  schedule_config?: Record<string, any>;
}

/**
 * Extended User type with subscriptions (not in generated OpenAPI)
 */
export interface UserWithSubscriptions {
  id: number | string;
  email: string;
  username?: string;
  first_name?: string;
  last_name?: string;
  firstname?: string; // Legacy property name
  lastname?: string; // Legacy property name
  phone?: string;
  subscriptions?: Array<{
    subscription_id?: string;
    stripe_customer_id?: string;
    created_at?: string;
    status?: string;
    plan?: string;
  }>;
}

/**
 * Extended Campaign type with additional properties not in OpenAPI schema
 * This includes both NestJS and Django backend properties
 */
export interface ExtendedCampaign {
  // Core identifiers
  id?: string | number;
  campaign_id?: number;
  name: string;
  description?: string;

  // Status
  status?: string | number | 'draft' | 'active' | 'paused' | 'completed' | 'failed';

  // Campaign type - can be object (NestJS) or string/number (Django)
  campaign_type?: CampaignTypesDto | string | number | null;
  campaign_type_id?: number;

  // Filters and scrapers
  camp_filters?: CampFilterDto[] | null;
  campaign_scrapers?: CampaignScrapersDto[];

  // Configuration
  config?: any;
  filters?: any;

  // Scheduling (allow null for CampaignRead compatibility)
  schedule_type?: string | null;
  schedule_config?: any;
  scheduling?: {
    repeat?: 'Once' | 'Weekly' | 'Monthly';
    repeat_count?: number;
    start_date?: string;
    days?: number[];
  } | null;
  campaign_start?: Array<{ campaign_start: string }>;

  // Tokens and leads
  reserved_tokens?: number;
  used_tokens?: number;
  total_leads?: number;
  unique_leads?: number;
  leads_count?: number;
  jobs_count?: number;

  // Email integration
  email_integration?: string | any;

  // Metrics
  metrics?: {
    replies?: number;
    sent?: number;
    opened?: number;
    clicked?: number;
  };

  // Timestamps
  created_at?: string;
  updated_at?: string;
}

// Alias for backward compatibility
export type Campaign = ExtendedCampaign;

/**
 * Extended paginated leads data with additional stats
 */
export interface ExtendedPaginatedLeadsData {
  data?: any[];
  results?: any[];
  total: number;
  count?: number;
  page: number;
  limit: number;
  totalPages: number;
  next?: string | null;
  previous?: string | null;
}

// ============================================================================
// EXTENDED TYPES (for incomplete generated types)
// ============================================================================

/**
 * Extended CampaignRead with properties not in OpenAPI schema
 * Use this type instead of CampaignRead for full compatibility
 */
export interface ExtendedCampaignRead {
  // From generated CampaignRead
  id: string;
  name: string;
  status?: string;
  campaign_type?: string | number | CampaignTypesDto | null;
  config?: any;
  filters?: any;
  schedule_type?: string;
  schedule_config?: any;
  reserved_tokens?: number;
  used_tokens?: number;
  total_leads?: number;
  unique_leads?: number;
  leads_count?: number;
  jobs_count?: number;
  email_integration?: string | any;
  created_at?: string;
  updated_at?: string;
  // Additional properties not in OpenAPI
  campaign_id?: number;
  campaign_type_id?: number;
  camp_filters?: CampFilterDto[] | null;
  campaign_scrapers?: CampaignScrapersDto[];
  description?: string;
  scheduling?: {
    repeat?: 'Once' | 'Weekly' | 'Monthly';
    repeat_count?: number;
    start_date?: string;
    days?: number[];
  } | null;
  campaign_start?: Array<{ campaign_start: string }>;
  metrics?: {
    replies?: number;
    sent?: number;
    opened?: number;
    clicked?: number;
  };
}

/**
 * Extended LeadsListApiResponse with pagination
 * The generated type is just LeadRead[], but Django returns paginated response
 */
export interface ExtendedLeadsListResponse {
  count?: number;
  total?: number;
  page?: number;
  limit?: number;
  totalPages?: number;
  next?: string | null;
  previous?: string | null;
  results?: any[];
  data?: any[];
}

// ============================================================================
// TYPE RE-EXPORTS
// ============================================================================

export type {
  // Auth types
  AuthLoginCreateApiArg as LoginApiArg,
  AuthLoginCreateApiResponse as LoginApiResponse,
  AuthRegisterCreateApiArg as RegisterApiArg,
  AuthRegisterCreateApiResponse as RegisterApiResponse,

  // User types
  UserRead,
  UserRegistrationRequest,
  PatchedUserRequest as UpdateUserRequest,

  // Campaign types (Campaign is defined locally as ExtendedCampaign)
  CampaignRead,
  CampaignRequest,
  PatchedCampaignRequest,
  CampaignsListApiResponse,
  CampaignsCreateApiArg,
  CampaignsRetrieveApiResponse as CampaignResponse,
  CampaignsPartialUpdateApiArg,
  CampaignsDestroyApiArg,

  // Lead types
  Lead,
  LeadRead,
  LeadRequest,
  PatchedLeadRequest,
  LeadsListApiResponse,
  LeadsMineRetrieveApiResponse,
  LeadsDatabaseRetrieveApiResponse,

  // Billing types (Note: BillingBalanceRetrieveApiResponse is 'unknown' in generated types)
  BillingTransactionsRetrieveApiResponse as TransactionsResponse,
  BillingCheckoutCreateApiArg,
  BillingAddTokensCreateApiArg,

  // Campaign update types
  CampaignsUpdateApiArg,
  CampaignsPartialUpdateApiArg as UpdateCampaignApiArg,

  // Marketplace types
  MarketplacePurchaseCreateApiArg,

  // Campaign filter types
  CampaignFiltersRetrieveApiResponse as CampaignFiltersResponse,
  CampaignSourcesRetrieveApiResponse as CampaignSourcesResponse,

  // Organization types
  Organization,
  OrganizationRequest,
  PatchedOrganizationRequest,

  // Scraper types (ScraperTemplate types no longer in schema)
  ScraperSchedule,
  ScraperScheduleRead,
  ScraperScheduleRequest,
  ScraperTask,
  ScraperTaskRead,
  ScraperTaskRequest,

  // Job types (JobsListApiResponse no longer in schema)
  JobsRetrieveApiResponse as JobResponse,

  // Token types
  TokenRefreshRequest,
} from './generatedApi';

// Legacy job types - aliased for backwards compatibility
export type JobsListResponse = unknown;
export type NestJsCampaignTypeRead = Record<string, unknown>;

// Legacy type aliases
export type User = import('./generatedApi').UserRead;
export type GetCampaignFiltersApiResponse = CampaignFiltersDto[];
export type CampaignsListResponse = import('./generatedApi').CampaignsListApiResponse;

// Removed scraper template types - aliased for backwards compatibility
export type ScraperTemplate = Record<string, unknown>;
export type ScrapeTemplateRequest = Record<string, unknown>;
export type PatchedScrapeTemplateRequest = Record<string, unknown>;

// Email account types (matching emailApi.ts interface)
export interface WarmupProfile {
  id: string;
  currentDailyLimit: number;
  targetDailyLimit: number;
  warmupPercentage: number;
  warmupStatus: 'WARMING' | 'COMPLETED' | 'PAUSED';
  daysCompleted: number;
  totalDays: number;
  warmupReputation?: number;
}

export interface EmailAccount {
  id: string;
  email: string;
  fromName?: string;
  providerType: 'OAUTH' | 'IMAP_SMTP';
  status: 'ACTIVE' | 'FAILED' | 'DISABLED' | 'WARMING_UP';
  maxEmailPerDay?: number;
  warmupEnabled?: boolean;
  warmupProfile?: WarmupProfile;
  createdAt: string;
  updatedAt: string;
}

// ============================================================================
// LEGACY TYPES FROM api.ts (for backward compatibility during migration)
// ============================================================================

/**
 * Subscription entity
 */
export interface Subscription {
  id?: number;
  created_at?: string;
  updated_at?: string;
  subscription_id?: string;
  users_id?: number;
  stripe_customer_id?: string;
  is_annual?: boolean;
  status?: string;
  plan?: string;
}

/**
 * Create user DTO
 */
export interface CreateUserDto {
  firstname: string;
  lastname: string;
  password: string;
  phone: string;
  email: string;
}

/**
 * Update user DTO
 */
export interface UpdateUserDto {
  firstname?: string;
  lastname?: string;
  password?: string;
  phone?: string;
  email?: string;
}

/**
 * Transactions DTO for billing history
 */
export interface TransactionsDto {
  billing_transactions_id?: number | string;
  paid_tokens?: number;
  reserved_tokens?: number;
  billing_id_billing?: number;
  campaign_id_campaign?: string | number | null;
  task_id_tasks?: number;
  created_at?: string;
  invoice_id?: string;
  receipt_url?: string;
  description?: string;
  transaction_type?:
    | 'topup'
    | 'subscription'
    | 'campaign_start'
    | 'purchase_leads'
    | 'campaign_refund'
    | 'email_verification';
  campaign?: { name: string } | null;
}

/**
 * Paginated billing transactions
 */
export interface PaginatedBillingTransactionsDto {
  total: number;
  data: TransactionsDto[];
  page: number;
  limit: number;
  totalPages: number;
}

/**
 * Balance DTO (alias)
 */
export type BalanceDto = Balance;

/**
 * Tokens DTO for JWT tokens
 */
export interface TokensDto {
  access: string;
  refresh: string;
}

/**
 * Auth user DTO for login
 */
export interface AuthUserDto {
  email: string;
  password: string;
}

/**
 * Forgot password DTO
 */
export interface ForgotPasswordDto {
  email: string;
  redirectURL?: string;
}

/**
 * Reset password DTO
 */
export interface ResetPasswordDto {
  password: string;
  repeatPassword: string;
}

/**
 * Change password DTO
 */
export interface ChangePasswordDto {
  password: string;
  repeatPassword: string;
}

/**
 * Confirm email DTO
 */
export interface ConfirmEmailDto {
  code: string;
}

/**
 * Refresh token DTO
 */
export interface RefreshTokenDto {
  refreshToken: string;
}

/**
 * Scrapers DTO
 */
export interface ScrapersDto {
  scrapers_id?: number;
  scraper_name?: string;
  site?: string;
  price?: number;
  campaign_type_id_campaign_type?: number;
  id?: number;
  name?: string;
}

/**
 * Campaign scrapers DTO
 */
export interface CampaignScrapersDto {
  id?: number;
  campaign_id?: number;
  scraper_id?: number;
  leads_count?: number;
  scrapers?: ScrapersDto;
}

/**
 * Leads entity
 */
export interface LeadsEntity {
  leads_id?: number;
  id?: string | number;
  name?: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  website?: string;
  source?: string;
  campaign_id?: number;
  created_at?: string;
  updated_at?: string;
  // Additional fields
  company?: string;
  title?: string;
  industry?: string;
  linkedin?: string;
  facebook?: string;
  twitter?: string;
  description?: string;
  rating?: number;
  reviews?: number;
  price_range?: string;
  categories?: string[];
  hours?: any;
  images?: string[];
  latitude?: number;
  longitude?: number;
  // Verification fields
  email_verified?: boolean;
  phone_verified?: boolean;
  // Marketplace fields
  is_purchased?: boolean;
  purchased?: boolean; // Alias for is_purchased
  purchase_date?: string;
}

// Alias for backward compatibility
export type ExtendedLeadsEntity = LeadsEntity;

/**
 * Verify email DTO
 */
export interface VerifyEmailDto {
  emails: string[];
  level?: 'simple' | 'standard' | 'premium';
}

/**
 * Export campaign leads DTO
 */
export interface ExportCampaignLeadsDto {
  campaignId?: number | string;
  campaign_id?: number | string;
  scraperId?: number;
  scraper_id?: number;
  campaignType?: number;
  format?: 'csv' | 'xlsx' | 'json';
  filters?: string | Record<string, any>;
  selectedLeadIds?: (string | number)[];
  leadIds?: (string | number)[];
}

/**
 * Task DTO
 */
export interface TaskDto {
  id?: number;
  task_id?: string;
  campaign_id?: number;
  status?: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  progress?: number;
  total_items?: number;
  processed_items?: number;
  created_at?: string;
  updated_at?: string;
  error_message?: string;
}

/**
 * Update task DTO
 */
export interface UpdateTaskDto {
  status?: string;
  progress?: number;
}

/**
 * Create lead DTO
 */
export interface CreateLeadDto {
  name?: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  website?: string;
  source?: string;
  campaign_id?: number;
  [key: string]: any;
}

/**
 * Paginated campaigns response
 */
export interface PaginatedCampaignsDto {
  total: number;
  data: ExtendedCampaign[];
  page: number;
  limit: number;
  totalPages: number;
}
